/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AppError, ErrorCodes } from "@tivocorp/data-domain-lib";

import {
  AxiosErrorAdapter,
  IErrorAdapter
} from "../../model/errorMapper/adapters";
import { Locale, LocaleKeys } from "../../model/locale";
import { OperationalLogger } from "../../model/logging";
import { ErrorCodeUtil } from "../../model/utility/ErrorCodeUtil";
import { interpolate } from "./myHubSavedMappers";

/**
 * Maps any error thrown from a MyHub service call to a composite
 * `"<code>[-<subcode>]"` code, via {@link AppError.getUIErrorwithSubErrorCode}.
 *
 * `code` is always an `ErrorTypes` constant — one per call, as the My Hub
 * error-code page (UAN-4717) assigns them:
 *   - `MY_SHOWS_ERROR_A754` — carousels, load-more, Saved/Deleted tabs;
 *   - `MY_SHOWS_FILTER_ERROR_A755` — the filter/tab strip;
 *   - `MY_HUB_MANAGED_RECORDINGS_ERROR_A781` — the series folder;
 *   - `MY_HUB_STORAGE_REQUEST_FAILED_R505` — the storage readout (log-only);
 *   - `RECORDING_ACTION_ERROR_R323` — recording writes, single and bulk;
 *   - `ONEPASS_ACTION_ERROR_R325` — OnePass writes.
 *
 * Extends `AppError` directly — constructing an instance *is* the mapping,
 * mirroring how the codebase's other `AppError` subclasses resolve their code
 * inside their own constructor (e.g. `ClaimServiceError`). The sub-error code
 * itself is resolved by the shared {@link ErrorCodeUtil}, the same utility
 * `HomeScreenErrorMapper` uses — it identifies the layer that failed (N6xx
 * transport, S6xx service context, G4xx-G6xx HTTP status) from any thrown
 * error, independent of the top-level code, so the same failure always
 * reports the same sub-code regardless of which call saw it.
 */
export class MyHubErrorMapper extends AppError {
  private static readonly adapter: IErrorAdapter = new AxiosErrorAdapter();

  public constructor(error: unknown, code: string) {
    const subErrorCode: string = ErrorCodeUtil.extractSubErrorCode(
      error,
      MyHubErrorMapper.adapter
    );
    super(code, MyHubErrorMapper.extractMessage(error), subErrorCode);
  }

  /**
   * Best available diagnostic text for a thrown error: an `AppError`'s
   * message — those do not extend `Error`, so `error.message` alone would
   * miss them — then a plain `Error.message`, then empty.
   */
  private static extractMessage(error: unknown): string {
    if (error === null || error === undefined || typeof error !== "object") {
      return "";
    }
    const candidate = error as {
      getMessage?: () => unknown;
      message?: unknown;
    };
    if (typeof candidate.getMessage === "function") {
      const message: unknown = candidate.getMessage();
      if (typeof message === "string" && message.length > 0) {
        return message;
      }
    }
    return typeof candidate.message === "string" ? candidate.message : "";
  }

  /**
   * True when the resolved sub-error code identifies a connectivity-level
   * failure rather than a service/HTTP one — covers both `baseInteractor`'s
   * own offline/reachability throw (tagged `errorCodeType: CONNECTION_FAILED`)
   * and a real axios transport error (`ECONNABORTED`/`ERR_NETWORK`/
   * `ENETUNREACH`/`EHOSTUNREACH`), since {@link ErrorCodeUtil} maps all of
   * those to the same `ErrorCodeTypes.CONNECTION_FAILED` → this suffix.
   */
  public isConnectivityFailure(): boolean {
    return this.getSubErrorCode() === ErrorCodes.NETWORK_CONNECTION_FAILED;
  }
}

/**
 * Renders `defaultKey`'s copy — or the connectivity-specific message, for an
 * offline/dropped-connection failure — with the mapped composite error code
 * interpolated in.
 */
function formatMyHubError(
  error: unknown,
  code: string,
  defaultKey: string
): string {
  const mapped = new MyHubErrorMapper(error, code);
  const key: string = mapped.isConnectivityFailure()
    ? LocaleKeys.MY_HUB_NETWORK_ERROR_MESSAGE
    : defaultKey;
  return interpolate(
    Locale.getString(key),
    mapped.getUIErrorwithSubErrorCode()
  );
}

/**
 * Toast copy for any MyHub failure: the shared "request cannot be processed"
 * message (or the connectivity-specific one, for an offline/dropped
 * connection), with the mapped composite error code appended so a repeated
 * failure is reportable/diagnosable instead of just a generic sentence.
 */
export function getMyHubErrorToastMessage(
  error: unknown,
  code: string
): string {
  return formatMyHubError(error, code, LocaleKeys.MY_HUB_REQUEST_FAILED_TOAST);
}

/**
 * Copy for a failed list/detail fetch, rendered inline where the list would
 * otherwise be (`MyHubSavedList.tsx`) — mirrors My Shows' own
 * `MY_SHOWS_ERROR_MESSAGE` convention (one templated string, reused for
 * every list-fetch failure) rather than showing the raw `Error.message`; also
 * swaps in the connectivity-specific message for an offline/dropped
 * connection, same as the toast above.
 */
export function getMyHubErrorMessage(error: unknown, code: string): string {
  return formatMyHubError(error, code, LocaleKeys.MY_HUB_ERROR_MESSAGE);
}

/**
 * Base path for every MyHub data-fetch endpoint (`GET /v1/myHub/...`) — used
 * as both the screen id and API endpoint for operational logging, the same
 * way `HomeScreenErrorMapper`'s screen uses one path for both.
 */
const MY_HUB_API_ENDPOINT: string = "/v1/myHub";

/**
 * Operational logger bound to MyHub's API surface — the always-on channel
 * fleet dashboards read, alongside the toast/inline copy above.
 */
const MyHubOperationalLogger = OperationalLogger.create({
  screenId: MY_HUB_API_ENDPOINT,
  apiEndpoint: MY_HUB_API_ENDPOINT
});

/**
 * Reports a failed MyHub service call to the operational logger, carrying the
 * mapped composite code (e.g. `A754-G500`) so a support call can be matched
 * to a row — same "apiRequest" channel `HomeScreenErrorMapper` reports
 * through. `operation` disambiguates MyHub's several distinct endpoints,
 * since one screen-scoped logger here covers all of them (unlike Home
 * Screen's single carousel fetch).
 */
export function logMyHubApiFailure(
  error: unknown,
  code: string,
  operation: string
): void {
  const mapped = new MyHubErrorMapper(error, code);
  const subErrorCode: string = mapped.getSubErrorCode();
  MyHubOperationalLogger.logApiFailure(error, {
    errorCode: mapped.getCode(),
    subErrorCode: subErrorCode.length > 0 ? subErrorCode : undefined,
    extra: { operation }
  });
}
